/**
 * The bottom panel: the loader's scheduling tabs, shared by Operations,
 * Analysis and Loader. In Overview and Maintenance the same tab buttons stay
 * (so a choice made there carries over) and the body says where the panels
 * live, with a button that goes to Operations on the chosen tab.
 */
import { useNavigate } from 'react-router';
import { ArrowRight, ChevronDown, ChevronUp, Maximize2, Minimize2 } from 'lucide-react';
import { useUI, type Mode } from '@/state/store';
import { IconButton, ScrollFade, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useLoader } from '@/features/loader/store';
import { GanttPanel } from '@/features/loader/ui/panels/GanttPanel';
import { MatrixPanel } from '@/features/loader/ui/panels/MatrixPanel';
import { PrioritiesPanel } from '@/features/loader/ui/panels/PrioritiesPanel';
import { GapsPanel } from '@/features/loader/ui/panels/GapsPanel';
import { AosLosPanel } from '@/features/loader/ui/panels/AosLosPanel';
import { ConflictsPanel } from '@/features/loader/ui/panels/ConflictsPanel';
import { BudgetPanel } from '@/features/loader/ui/panels/BudgetPanel';
import { PlanetPanel } from '@/features/loader/ui/panels/PlanetPanel';

interface LoaderTab {
  key: string;
  label: string;
  render: () => React.ReactNode;
  /** Only for these networks (undefined means every network). */
  networks?: string[];
}

const LOADER_TABS: LoaderTab[] = [
  { key: 'gantt', label: 'Gantt', render: () => <GanttPanel /> },
  { key: 'matrix', label: 'Loading matrix', render: () => <MatrixPanel /> },
  { key: 'conflicts', label: 'Conflicts', render: () => <ConflictsPanel /> },
  { key: 'priorities', label: 'Priorities', render: () => <PrioritiesPanel /> },
  { key: 'gaps', label: 'Coverage gaps', render: () => <GapsPanel /> },
  { key: 'aoslos', label: 'AOS/LOS', render: () => <AosLosPanel /> },
  { key: 'budget', label: 'Budget', render: () => <BudgetPanel /> },
  { key: 'moon', label: 'Moon', render: () => <PlanetPanel body="moon" />, networks: ['artemis'] },
  { key: 'mars', label: 'Mars', render: () => <PlanetPanel body="mars" />, networks: ['mars'] },
];

const GLOBE_MODES = new Set<Mode>(['loader', 'operations', 'analysis']);
/** Tabs the slim noaa-core profile leaves out. */
const SLIM_HIDDEN = new Set(['budget', 'gaps', 'moon', 'mars']);

const HEIGHT = { collapsed: 'h-9', default: 'h-[280px]', expanded: 'h-[62vh]' } as const;

const tabClass = (active: boolean) =>
  cn(
    'shrink-0 whitespace-nowrap rounded-control px-2.5 py-1 text-xs transition hover:bg-overlay hover:text-ink',
    active ? 'bg-accent-soft text-accent' : 'text-ink-3',
  );

/** Open or collapse, expand or restore: pinned at the right of the tab row, outside its scroll. */
function PanelControls({
  state,
  setState,
}: {
  state: 'collapsed' | 'default' | 'expanded';
  setState: (s: 'collapsed' | 'default' | 'expanded') => void;
}) {
  return (
    <div className="flex shrink-0 items-center border-l border-hairline pl-1">
      <IconButton
        label={state === 'collapsed' ? 'Open panel ([)' : 'Collapse panel ([)'}
        aria-expanded={state !== 'collapsed'}
        onClick={() => setState(state === 'collapsed' ? 'default' : 'collapsed')}
      >
        {state === 'collapsed' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </IconButton>
      <IconButton
        label={state === 'expanded' ? 'Restore height' : 'Expand panel'}
        onClick={() => setState(state === 'expanded' ? 'default' : 'expanded')}
      >
        {state === 'expanded' ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </IconButton>
    </div>
  );
}

/** Overview and Maintenance: where the scheduling panels live, and a way there that keeps the chosen tab. */
function ElsewhereState({ onGo }: { onGo: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-panel text-center text-data">
      <p className="text-ink-2">Scheduling panels apply in Operations, Analysis and Loader.</p>
      <TextButton onClick={onGo} className="flex items-center gap-1 bg-accent-soft text-accent">
        Go to Operations
        <ArrowRight aria-hidden className="h-3.5 w-3.5" />
      </TextButton>
    </div>
  );
}

export function BottomPanel({ mode }: { mode: Mode }) {
  const state = useUI((s) => s.bottomPanel);
  const tab = useUI((s) => s.bottomTab);
  const setTab = useUI((s) => s.setBottomTab);
  const setState = useUI((s) => s.setBottomPanel);
  const network = useLoader((s) => s.network);
  const profile = useLoader((s) => s.profile);
  const navigate = useNavigate();
  if (network === 'full_catalogue' && mode === 'loader') return null;
  const relevant = GLOBE_MODES.has(mode);
  // The same tabs in every mode, so a tab chosen in Overview or Maintenance exists in Operations.
  const tabs = LOADER_TABS.filter(
    (t) => (!t.networks || t.networks.includes(network)) && (profile === 'full' || !SLIM_HIDDEN.has(t.key)),
  );
  const current = tabs.find((t) => t.key === tab) ?? tabs[0];

  return (
    <section
      aria-label="Scheduling panels"
      data-testid="bottom-panel"
      className={cn(
        'flex shrink-0 flex-col border-t border-hairline bg-raised transition-[height] duration-200',
        HEIGHT[state],
      )}
    >
      <div className="flex h-9 shrink-0 items-center gap-1 pl-2 pr-1">
        <ScrollFade className="flex-1" innerClassName="gap-1" activeKey={current.key} role="group" label="Scheduling panel tabs">
          {tabs.map((t) => {
            const active = t.key === current.key && state !== 'collapsed';
            return (
              <button
                key={t.key}
                type="button"
                aria-pressed={active}
                data-active={t.key === current.key ? 'true' : undefined}
                onClick={() => setTab(t.key)}
                className={tabClass(active)}
              >
                {t.label}
              </button>
            );
          })}
        </ScrollFade>
        <PanelControls state={state} setState={setState} />
      </div>
      {state !== 'collapsed' && (
        <div role="region" aria-label={current.label} className="min-h-0 flex-1 overflow-hidden border-t border-hairline">
          {relevant ? (
            <div className="h-full overflow-y-auto scrollbar-thin" key={current.key}>
              {current.render()}
            </div>
          ) : (
            <ElsewhereState
              onGo={() => {
                setTab(current.key);
                navigate('/operations');
              }}
            />
          )}
        </div>
      )}
    </section>
  );
}
